import { memo } from 'react'
import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { Truck } from 'lucide-react'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import type { DriverClusterPoint, StateActivity } from '../types/boxTruckMap'

interface StateActivityMarkerProps {
    state: StateActivity
    selected: boolean
    onSelect: (stateName: string) => void
}

export const StateActivityMarker = memo(({ state, selected, onSelect }: StateActivityMarkerProps) => (
    <AdvancedMarker position={state.position} zIndex={1} clickable>
        <button
            type="button"
            onClick={() => onSelect(state.name)}
            className={`min-w-20 rounded-xl px-2 py-1.5 text-center shadow-overlay transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${selected ? 'bg-accent text-accent-foreground' : 'bg-surface text-foreground'}`}
            aria-label={`${state.name}: ${state.loads === null ? 'load count not loaded' : `${state.loads} active loads`} and ${state.drivers} drivers`}
        >
            <span className="block text-[11px] font-semibold">{state.code}</span>
            <span className="block text-[10px]">{state.loads === null ? 'Select for loads' : `${state.loads.toLocaleString()} loads`}</span>
        </button>
    </AdvancedMarker>
))

StateActivityMarker.displayName = 'StateActivityMarker'

interface DriverClusterMarkerProps {
    point: DriverClusterPoint
    onSelect: (point: DriverClusterPoint) => void
}

const getClusterSize = (count: number): string => {
    if (count >= 100) return 'h-14 w-14'
    if (count >= 25) return 'h-12 w-12'
    return 'h-10 w-10'
}

export const DriverClusterMarker = memo(({ point, onSelect }: DriverClusterMarkerProps) => (
    <AdvancedMarker position={point.position} zIndex={2} clickable>
        <button
            type="button"
            onClick={() => onSelect(point)}
            aria-label={`${point.count} drivers in this area. Zoom in to inspect.`}
            className={`flex items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-foreground shadow-overlay transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${getClusterSize(point.count)}`}
        >
            {point.countLabel}
        </button>
    </AdvancedMarker>
))

DriverClusterMarker.displayName = 'DriverClusterMarker'

interface DriverMapMarkerProps {
    driver: BoxTruckMapDriver
    selected: boolean
    onSelect: (driverId: string) => void
}

export const DriverMapMarker = memo(({ driver, selected, onSelect }: DriverMapMarkerProps) => {
    const truckNumber = driver.truckNumber ? `Truck #${driver.truckNumber.replace(/^#/, '')}` : ''
    const secondaryLabel = [driver.equipment, truckNumber].filter(Boolean).join(' · ')

    return (
        <AdvancedMarker position={{ lat: driver.lat, lng: driver.lng }} zIndex={selected ? 100 : 3} clickable>
            <div className="group relative flex flex-col items-center">
                {(driver.name || secondaryLabel) && (
                    <div
                        className={`pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded-lg bg-surface px-2 py-1 text-center shadow-overlay ${selected ? 'block' : 'hidden group-hover:block group-focus-within:block'}`}
                    >
                        {driver.name && (
                            <span className="block max-w-[176px] truncate text-[11px] font-semibold leading-tight text-foreground">{driver.name}</span>
                        )}
                        {secondaryLabel && (
                            <span className="block max-w-[176px] truncate text-[10px] leading-tight text-foreground-muted">{secondaryLabel}</span>
                        )}
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => onSelect(driver.driverId)}
                    className={`flex h-9 w-9 items-center justify-center rounded-full shadow-overlay transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${driver.verified ? 'bg-success text-success-foreground' : 'bg-warning text-warning-foreground'} ${selected ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface' : ''}`}
                    aria-label={`Open ${driver.name || 'driver'} details${driver.verified ? '' : ' (unverified)'}`}
                >
                    <Truck size={18} aria-hidden />
                </button>
            </div>
        </AdvancedMarker>
    )
})

DriverMapMarker.displayName = 'DriverMapMarker'
